import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-apps/shared/src/components/ui/tooltip";
import { AccountTypeBadge } from "@solomon-apps/shared/src/components/Yaas/AccountTypeBadge";
import { TooltipIcon } from "@solomon-labs/design/components/icons";
import { Badge } from "@solomon-labs/design/components/ui";
import React from "react";

import type { MultisigSummary, OwnershipSourceKind } from "../steps";

/**
 * The account-type badge below the position-source field: "Wallet" for an
 * EOA; for a Squads source, named by what was pasted: "Squads Account
 * (threshold/members)" or "Squads Vault (threshold/members)",
 * plus the authorized-signers tooltip.
 * Reads the detection the flow already ran; no lookups of its own.
 *
 * When the source already has a position, an "Already Registered" chip sits
 * beside the type badge: the error-tone filled chip with no indicator dot.
 * That is StatusBadge `tone="error" variant="filled"` minus its dot, which
 * StatusBadge cannot drop, so it is the DS `Badge` StatusBadge renders
 * (`variant="fill" color="error"`), per its intent ("a chip with no dot, use
 * Badge directly").
 */
export function SourceDetectionSummary({
    sourceKind,
    connectedWalletAddress,
    multisigSummary,
    enteredAddress = "",
    alreadyRegistered = false
}: {
    sourceKind: OwnershipSourceKind | null;
    connectedWalletAddress: string | null;
    multisigSummary: MultisigSummary | null;
    /** What the user pasted: the vault address names the badge "Squads
     *  Vault"; anything else (the multisig account) "Squads Account". */
    enteredAddress?: string;
    /** The source already has a position with this Company Recipient. */
    alreadyRegistered?: boolean;
}): React.ReactElement | null {
    const registeredBadge = alreadyRegistered ? (
        <Badge variant="fill" color="error">
            Already Registered
        </Badge>
    ) : null;

    if (sourceKind === "eoa") {
        return (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <AccountTypeBadge type="wallet" />
                {registeredBadge}
            </div>
        );
    }

    if (sourceKind !== "multisig-squads" || multisigSummary === null) {
        return null;
    }

    const members = multisigSummary.members ?? [];
    // Members who can verify can sign to prove control: Squads "Voters" (Vote)
    // or "Executors" (Execute). Proposer-only members (Initiate without Vote or
    // Execute) are not authorized.
    const authorizedSigners = members.filter((member) => member.canVerify);
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <AccountTypeBadge
                type={
                    multisigSummary.vaultAddress !== undefined &&
                    enteredAddress.trim() === multisigSummary.vaultAddress
                        ? "squads-vault"
                        : "squads-multisig"
                }
                multisig={{
                    threshold: multisigSummary.threshold,
                    memberCount: multisigSummary.memberCount
                }}
                trailing={
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <span className="inline-flex cursor-help">
                                        <TooltipIcon className="size-3.5" />
                                    </span>
                                }
                            />
                            <TooltipContent side="top" maxWidth="2xl">
                                {members.length > 0 ? (
                                    <div className="flex flex-col gap-1.5">
                                        <Text
                                            variant="label"
                                            className="text-foreground-inverse-muted"
                                        >
                                            Authorized to Sign:
                                        </Text>
                                        {authorizedSigners.map((member) => {
                                            const isConnected =
                                                connectedWalletAddress ===
                                                member.address;
                                            return (
                                                <Text
                                                    key={member.address}
                                                    variant="number"
                                                    render={<span />}
                                                    className="whitespace-nowrap"
                                                >
                                                    {member.address}
                                                    {isConnected
                                                        ? " (You)"
                                                        : ""}
                                                </Text>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Text variant="small">
                                        Signer list unavailable; membership will
                                        be checked again on submit.
                                    </Text>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                }
            />
            {registeredBadge}
        </div>
    );
}
